import { Head, Link } from "@inertiajs/react"
import { Gift } from "lucide-react"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import {
  CategoryChipBar,
  type CategoryFilter,
} from "@/components/coins/category-chip-bar"
import { FlagshipHero } from "@/components/coins/flagship-hero"
import { RedeemSheet } from "@/components/coins/redeem-sheet"
import type { RedeemProfileAddress } from "@/components/coins/redeem-ship-to"
import {
  RewardCard,
  type RewardCardReward,
  type RewardCategory,
} from "@/components/coins/reward-card"
import { WalletHero, type WalletRank } from "@/components/coins/wallet-hero"
import {
  type RedemptionHistoryItem,
  RedemptionHistoryRow,
} from "@/components/creator/rewards/redemption-history-row"
import { Card, CardContent } from "@/components/ui/card"
import AppLayout from "@/layouts/app-layout"
import { creatorMissionsPath, creatorRedemptionsPath } from "@/routes"

/**
 * OKS-556 — Creator/Rewards/Index.tsx (`/rewards`) — the §19 Rewards Wallet.
 *
 * Evolves the OKS-407 tabbed catalog into a single-scroll wallet (UX Spec §2):
 * WalletHero (gold balance + XP-derived rank) → flagship hero → category chips
 * → grouped RewardCard sections → inline "Your Redemptions". The V1
 * All/Available/Coming-soon Tabs are replaced by the Gear/Experiences/Perks
 * category chips (client-side filter, TRD KD-8).
 *
 * # Brand isolation
 *
 * Every prop comes from `CreatorRewardsController#index` scoped to
 * `Current.brand` + `Current.membership` (catalogue, balance, rank,
 * redeemed_count, redemptions). A brand switch via `CrewSwitcher` triggers a
 * full Inertia visit → re-render with the new brand's wallet (PRD AC).
 *
 * # Redeem
 *
 * A RewardCard / flagship Redeem opens the OKS-557 `RedeemSheet` (§20); the
 * sheet posts with `preserveState`, so a successful redeem re-renders the page
 * with fresh `rewards` / `balance` / `redeemed_count` / `recent_redemptions`.
 */

const CATEGORY_ORDER: RewardCategory[] = ["gear", "experiences", "perks"]

interface BrandShape {
  id: number
  name: string
}

export interface RewardsPageProps {
  rewards: RewardCardReward[]
  balance: number
  brand: BrandShape
  // OKS-557 — current brand's per-mission coin award; drives the §20 sheet's
  // can't-afford / locked "~M missions to earn enough" estimate.
  coins_base_per_mission: number
  /**
   * OKS-985 — the creator's OWN profile address, prefilling the gear claim's
   * Ship-to block. Their own data on their own page; the brand-facing
   * disclosure is the gated `Shipments::AddressPayload` and is unrelated.
   */
  profile_address: RedeemProfileAddress | null
  cheapest_cost: number | null
  // OKS-556 — flagship reward id (highest-cost available), or null when the
  // brand has no available reward (hero omitted).
  flagship: number | null
  // OKS-556 — named rank + tier for the WalletHero RankPatch, or null for a
  // mis-seeded brand (patch omitted, balance still shows).
  rank: WalletRank | null
  // OKS-556 — recent redemptions for the inline "Your Redemptions" block.
  recent_redemptions: RedemptionHistoryItem[]
}

export default function RewardsIndex({
  rewards,
  balance,
  brand,
  coins_base_per_mission: coinsBasePerMission,
  profile_address,
  flagship,
  rank,
  recent_redemptions: recentRedemptions,
}: RewardsPageProps) {
  const { t } = useTranslation()
  const [selectedReward, setSelectedReward] = useState<RewardCardReward | null>(
    null,
  )
  const [filter, setFilter] = useState<CategoryFilter>("all")

  // OKS-557 — the redeem sheet posts with `preserveState: true`, so a
  // successful redeem already re-renders with fresh props; closing just
  // clears the selection.
  const closeSheet = () => setSelectedReward(null)

  const hasRewards = rewards.length > 0

  // Group the flat catalogue into category buckets, preserving each bucket's
  // server order (coin_cost asc). The flagship reward STAYS in its category
  // section (it's the canonical, `data-reward-id`-bearing card); the flagship
  // hero above is a featured DUPLICATE VIEW that deliberately carries NO
  // `data-reward-id`, so each reward-id resolves to exactly one element on the
  // page (OKS-557 E2E `[data-reward-id]` contract) while every category
  // section — even one whose only member is the flagship — stays populated.
  // Empty categories are dropped (UX §3 edge).
  const grouped = useMemo(() => {
    const map = new Map<RewardCategory, RewardCardReward[]>()
    for (const category of CATEGORY_ORDER) {
      const items = rewards.filter((r) => r.category === category)
      if (items.length > 0) map.set(category, items)
    }
    return map
  }, [rewards])

  const availableCategories = useMemo(
    () => CATEGORY_ORDER.filter((c) => grouped.has(c)),
    [grouped],
  )

  const visibleCategories =
    filter === "all"
      ? availableCategories
      : availableCategories.filter((c) => c === filter)

  const flagshipReward = useMemo(
    () =>
      flagship === null
        ? null
        : (rewards.find((r) => r.id === flagship) ?? null),
    [rewards, flagship],
  )

  return (
    <AppLayout>
      <Head title={t("rewards.head_title")} />

      <div className="container mx-auto max-w-2xl space-y-5 px-4 py-6">
        <WalletHero balance={balance} rank={rank} />

        {hasRewards ? (
          <>
            {flagshipReward && (
              <FlagshipHero
                reward={flagshipReward}
                balance={balance}
                onRedeem={setSelectedReward}
              />
            )}

            <CategoryChipBar
              available={availableCategories}
              selected={filter}
              onSelect={setFilter}
            />

            <div className="space-y-6">
              {visibleCategories.map((category) => (
                <section
                  key={category}
                  data-testid={`reward-category-section-${category}`}
                >
                  <h2
                    className="text-muted-foreground mb-2 text-sm font-semibold tracking-wide uppercase"
                    data-testid={`reward-category-heading-${category}`}
                  >
                    {t(`rewards.creator.categories.${category}`)}
                  </h2>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {grouped.get(category)!.map((reward) => (
                      <RewardCard
                        key={reward.id}
                        reward={reward}
                        onRedeem={setSelectedReward}
                      />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </>
        ) : (
          <EmptyState brandName={brand.name} />
        )}

        {recentRedemptions.length > 0 && (
          <YourRedemptions
            redemptions={recentRedemptions}
            profileAddress={profile_address}
          />
        )}
      </div>

      <RedeemSheet
        profileAddress={profile_address}
        reward={selectedReward}
        balance={balance}
        brandName={brand.name}
        coinsBasePerMission={coinsBasePerMission}
        onClose={closeSheet}
      />
    </AppLayout>
  )
}

function YourRedemptions({
  redemptions,
  profileAddress,
}: {
  redemptions: RedemptionHistoryItem[]
  profileAddress: RedeemProfileAddress | null
}) {
  const { t } = useTranslation()

  return (
    <section data-testid="your-redemptions" className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-lg font-semibold tracking-tight">
          {t("rewards.creator.your_redemptions.title")}
        </h2>
        <Link
          href={creatorRedemptionsPath()}
          data-testid="your-redemptions-see-all"
          className="text-primary hover:text-primary/80 text-sm font-medium underline-offset-4 hover:underline"
        >
          {t("rewards.creator.your_redemptions.see_all")}
        </Link>
      </div>
      <div className="space-y-3">
        {redemptions.map((redemption) => (
          <RedemptionHistoryRow
            key={redemption.id}
            redemption={redemption}
            profileAddress={profileAddress}
          />
        ))}
      </div>
    </section>
  )
}

function EmptyState({ brandName }: { brandName: string }) {
  const { t } = useTranslation()

  return (
    <Card
      className="border-dashed py-12"
      data-testid="creator-rewards-empty-state"
    >
      <CardContent className="flex flex-col items-center justify-center gap-3 text-center">
        <Gift aria-hidden="true" className="text-muted-foreground size-12" />
        <h2 className="text-lg font-semibold">
          {t("rewards.creator.empty_title")}
        </h2>
        <p className="text-muted-foreground text-sm">
          {t("rewards.creator.empty_body", { brand: brandName })}
        </p>
        <Link
          href={creatorMissionsPath()}
          data-testid="creator-rewards-empty-back-link"
          className="text-primary hover:text-primary/80 text-sm font-medium underline-offset-4 hover:underline"
        >
          {t("rewards.creator.empty_back_to_missions")}
        </Link>
      </CardContent>
    </Card>
  )
}
